import type { DayOfWeek } from 'app/typings/core/admin/enum/dayOfWeek';
import { DAY_OF_WEEK } from 'app/typings/core/admin/enum/dayOfWeek';
import type { ClosingPeriodType } from 'app/typings/core/admin/experiences/closingPeriod/closingPeriod';
import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import type { ExtranetHotelExperience } from 'app/typings/core/extranet/hotel/experiences';
import type {
  InventoryExperience,
  InventoryResponse,
} from 'app/typings/core/extranet/inventory/inventory';
import { formatDateWithoutHour } from 'app/utils/dates';
import {
  addDays,
  eachDayOfInterval,
  getDay,
  isAfter,
  isBefore,
  isWithinInterval,
  startOfDay,
} from 'app/utils/dayjs';

type ExperienceOpeningStatus = 'PUBLISHED' | 'UNPUBLISHED' | 'CLOSED';

type ExperienceOpeningMessage =
  | 'CLOSING_PERIOD'
  | 'CLOSED_BEFORE_CHECKIN'
  | 'PUBLISHED'
  | 'MISSING_STOCK'
  | 'PAST_SALES'
  | 'FUTURE_SALES'
  | 'STOCK_CLOSED'
  | 'MANUALLY_CLOSED';

type ExperienceOpeningStatusAndMessage = {
  inventoryStatus: ExperienceOpeningStatus;
  message: ExperienceOpeningMessage;
};

export type ExperienceOpening = InventoryExperience &
  ExperienceOpeningStatusAndMessage;

// Gets experience price, preferring daytime price if pack is daytime
export const getExperiencePrice = (
  experienceOpening: ExperienceOpening | undefined,
  isDaytime: boolean
): number => {
  if (!experienceOpening) {
    return 0;
  }

  if (isDaytime && experienceOpening.daytimePrice !== undefined) {
    return experienceOpening.daytimePrice;
  }

  return experienceOpening.price ?? 0;
};

// Determines experience opening status and message based on closing periods, sales window, and stock
const getExperienceOpeningStatusAndMessage = (
  isPastSales: boolean,
  isFutureSales: boolean,
  isDuringClosingPeriod: boolean,
  isClosedBeforeCheckinInterval: boolean,
  isAlwaysIncludedWithNight: boolean,
  isClosed?: boolean,
  remainingStock?: number | null
): ExperienceOpeningStatusAndMessage => {
  // Check sales window constraints
  if (isPastSales) {
    return { inventoryStatus: 'CLOSED', message: 'PAST_SALES' };
  }

  if (isFutureSales) {
    return { inventoryStatus: 'CLOSED', message: 'FUTURE_SALES' };
  }

  // Check closing periods first (highest priority)
  if (isDuringClosingPeriod) {
    return { inventoryStatus: 'CLOSED', message: 'CLOSING_PERIOD' };
  }

  // Check closed before check-in interval
  if (isClosedBeforeCheckinInterval) {
    return { inventoryStatus: 'CLOSED', message: 'CLOSED_BEFORE_CHECKIN' };
  }

  // Check if experience is closed on this day in admin configuration
  if (isClosed !== undefined && isClosed) {
    return { inventoryStatus: 'CLOSED', message: 'STOCK_CLOSED' };
  }

  // Always included experience with a stock (manually closed)
  if (
    isAlwaysIncludedWithNight &&
    remainingStock !== undefined &&
    remainingStock !== null
  ) {
    return { inventoryStatus: 'UNPUBLISHED', message: 'MANUALLY_CLOSED' };
  }

  if (remainingStock === undefined || remainingStock === 0) {
    return { inventoryStatus: 'UNPUBLISHED', message: 'MISSING_STOCK' };
  }

  return { inventoryStatus: 'PUBLISHED', message: 'PUBLISHED' };
};

// Checks if a date falls within any closing period
const isDateDuringClosingPeriod = (
  date: Date,
  closingPeriods: ClosingPeriodType[]
): boolean => {
  return closingPeriods.some((closingPeriod) =>
    isWithinInterval(startOfDay(date), {
      start: startOfDay(new Date(closingPeriod.start)),
      end: startOfDay(new Date(closingPeriod.end)),
    })
  );
};

// Calculates the interval during which experiences are closed before check-in
const getClosedBeforeCheckinInterval = (
  closedDaysBeforeCheckIn: number
): { start: Date; end: Date } => {
  const today = new Date();
  const start = new Date(today.setHours(0, 0, 0, 0));
  const end = addDays(new Date(), closedDaysBeforeCheckIn);

  end.setHours(23, 59, 59, 999);

  return { start, end };
};

// Checks if a date is within the closed-before-checkin interval
const isDateClosedBeforeCheckin = (
  date: Date,
  closedDaysBeforeCheckIn: number | undefined
): boolean => {
  if (closedDaysBeforeCheckIn === undefined) {
    return false;
  }

  const interval = getClosedBeforeCheckinInterval(closedDaysBeforeCheckIn);

  return isWithinInterval(date, interval);
};

const DAY_OF_WEEK_MAP: DayOfWeek[] = [
  DAY_OF_WEEK.SUNDAY,
  DAY_OF_WEEK.MONDAY,
  DAY_OF_WEEK.TUESDAY,
  DAY_OF_WEEK.WEDNESDAY,
  DAY_OF_WEEK.THURSDAY,
  DAY_OF_WEEK.FRIDAY,
  DAY_OF_WEEK.SATURDAY,
];

const getPricesFromOpeningConfiguration = (
  experience: ExtranetHotelExperience,
  date: Date
): { price?: number; daytimePrice?: number } => {
  const priceConfig = experience.openingConfiguration?.priceConfiguration;
  const isOriginal = experience.countryId !== undefined;

  if (!priceConfig) {
    return {};
  }

  if (priceConfig.type === OPENING_CONFIGURATION_TYPE.CONSTANT) {
    const { price, daytimePrice, staycationPrice } = priceConfig.configuration;

    return {
      price: isOriginal ? staycationPrice ?? price : price,
      daytimePrice,
    };
  }

  const dayOfWeek = DAY_OF_WEEK_MAP[getDay(date)];
  const dayConfig = priceConfig.days[dayOfWeek];

  return {
    price: isOriginal
      ? dayConfig?.staycationPrice ?? dayConfig?.price
      : dayConfig?.price,
    daytimePrice: dayConfig?.daytimePrice,
  };
};

// Computes experience opening for a single date
export const computeExperienceOnSingleDate = (
  saleEndDate: Date,
  date: Date,
  experience: ExtranetHotelExperience,
  inventoryExperience?: InventoryExperience
): ExperienceOpening => {
  const isDuringClosingPeriod = isDateDuringClosingPeriod(
    date,
    experience.closingPeriods ?? []
  );

  const isClosedBeforeCheckinInterval = isDateClosedBeforeCheckin(
    date,
    experience.closedDaysBeforeCheckIn
  );

  const isPastSales = isBefore(startOfDay(date), startOfDay(new Date()));
  const isFutureSales = isAfter(
    startOfDay(date),
    startOfDay(new Date(saleEndDate))
  );

  const defaultInventoryExperience: InventoryExperience = {
    ...getPricesFromOpeningConfiguration(experience, date),
    bookedStock: 0,
    remainingStock: null,
    isClosed: false,
    isStockOverridden: false,
  };

  const experienceData = inventoryExperience ?? defaultInventoryExperience;

  const statusAndMessage = getExperienceOpeningStatusAndMessage(
    isPastSales,
    isFutureSales,
    isDuringClosingPeriod,
    isClosedBeforeCheckinInterval,
    experience.isAlwaysIncludedWithNight,
    experienceData.isClosed,
    experienceData.remainingStock
  );

  return {
    ...statusAndMessage,
    ...experienceData,
  };
};

// Computes experience openings for all experiences on a specific date
const computeExperiencesOnDate = (
  saleEndDate: string,
  date: Date,
  experiences: ExtranetHotelExperience[],
  inventoryExperiences: { [experienceId: number]: InventoryExperience }
): Record<number, ExperienceOpening> => {
  return experiences.reduce((result, experience) => {
    const inventoryExperience = inventoryExperiences[experience.id];

    result[experience.id] = computeExperienceOnSingleDate(
      new Date(saleEndDate),
      date,
      experience,
      inventoryExperience
    );

    return result;
  }, {} as Record<number, ExperienceOpening>);
};

// Filters opening overrides to only include those for a specific date
const filterOpeningOverridesForDate = (
  inventoryInput: InventoryResponse,
  date: Date
): { [experienceId: number]: InventoryExperience } => {
  const dateKey = formatDateWithoutHour(date);

  return inventoryInput[dateKey]?.experiences ?? {};
};

// Main function: computes experience openings for a date range
export const computeExperiences = (
  saleEndDate: string,
  startDate: string,
  endDate: string,
  experiences: ExtranetHotelExperience[],
  inventoryInput: InventoryResponse
): Record<string, Record<number, ExperienceOpening>> => {
  const dates = eachDayOfInterval({
    start: new Date(startDate),
    end: new Date(endDate),
  });

  return dates.reduce((result, date) => {
    const dateKey = formatDateWithoutHour(date);
    const overridesForDate = filterOpeningOverridesForDate(
      inventoryInput,
      date
    );

    result[dateKey] = computeExperiencesOnDate(
      saleEndDate,
      date,
      experiences,
      overridesForDate
    );

    return result;
  }, {} as Record<string, Record<number, ExperienceOpening>>);
};
